import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guidesOpen, setGuidesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guidesRef = useRef<HTMLDivElement>(null);
  const mobilePanelRef = useRef<HTMLDivElement>(null);
  const hamburgerBtnRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  useEffect(() => {
    setDropdownOpen(false);
    setGuidesOpen(false);
    setMobileOpen(false);
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  // Click outside: ignore clicks on hamburger button to avoid toggle conflict
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setDropdownOpen(false);
      }
      if (guidesRef.current && !guidesRef.current.contains(target)) {
        setGuidesOpen(false);
      }
      if (
        mobilePanelRef.current &&
        !mobilePanelRef.current.contains(target) &&
        hamburgerBtnRef.current &&
        !hamburgerBtnRef.current.contains(target)
      ) {
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleMobile = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setMobileOpen((prev) => !prev);
  }, []);

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
  }, []);

  const guideSlugs = [
    { slug: "product-comparison-mastery", label: "Product Comparison Mastery" },
    { slug: "trend-spotting-2026", label: "Trend Spotting 2026" },
    { slug: "save-money-shopping", label: "Save Money Shopping" },
    { slug: "seasonal-shopping-guide", label: "Seasonal Shopping Guide" },
    { slug: "finding-hidden-gems", label: "Finding Hidden Gems" },
    { slug: "shopping-mistakes-avoid", label: "Shopping Mistakes to Avoid" },
    { slug: "category-deep-dive-shoes", label: "Shoes Deep Dive" },
    { slug: "category-deep-dive-apparel", label: "Apparel Deep Dive" },
    { slug: "beginner-to-expert", label: "Beginner to Expert" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-background-50/40 backdrop-blur-sm"
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <div className="flex items-center gap-2">
            <Link to="/" className="flex items-center gap-2 cursor-pointer">
              <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-store-2-line text-background-50 text-lg"></i>
              </div>
              <span className="font-heading text-lg md:text-xl font-semibold text-foreground-950 whitespace-nowrap">
                PandaBuy Boats
              </span>
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                location.pathname === "/"
                  ? "bg-background-100 text-foreground-950"
                  : "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
              }`}
            >
              Home
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => { setDropdownOpen(!dropdownOpen); setGuidesOpen(false); }}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Categories
                <i
                  className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-xl border border-background-200/70 py-2 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors duration-150 whitespace-nowrap"
                    >
                      <div className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                        <img
                          src={cat.thumbUrl}
                          alt={cat.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="relative" ref={guidesRef}>
              <button
                onClick={() => { setGuidesOpen(!guidesOpen); setDropdownOpen(false); }}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  location.pathname.startsWith("/guides/")
                    ? "bg-background-100 text-foreground-950"
                    : "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                }`}
              >
                Shopping Guides
                <i
                  className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                    guidesOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              {guidesOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-xl border border-background-200/70 py-2 max-h-96 overflow-y-auto">
                  {guideSlugs.map((g) => (
                    <Link
                      key={g.slug}
                      to={`/guides/${g.slug}`}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors duration-150 whitespace-nowrap"
                    >
                      {g.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              PandaBuy Sheet
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full border-2 border-primary-500 text-primary-600 text-sm font-medium hover:bg-primary-500 hover:text-background-50 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <div className="flex md:hidden items-center gap-2">
            <Link
              to="/"
              onClick={closeMobile}
              className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                location.pathname === "/"
                  ? "bg-background-100 text-foreground-950"
                  : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              <i className="ri-home-4-line text-lg"></i>
            </Link>
            <button
              ref={hamburgerBtnRef}
              onClick={toggleMobile}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-foreground-950/20"
          onClick={closeMobile}
        >
          <div
            className="md:hidden absolute top-16 left-0 right-0 bg-background-50 border-t border-background-200/70"
            ref={mobilePanelRef}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 max-h-[calc(100dvh-4rem)] overflow-y-auto">
              <div className="space-y-1 mb-3">
                <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wide px-2 py-1">
                  Navigation
                </p>
                <Link
                  to="/"
                  onClick={closeMobile}
                  className={`flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors ${
                    location.pathname === "/"
                      ? "bg-background-100 text-foreground-950 font-medium"
                      : "text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  <i className="ri-home-4-line text-base"></i>
                  Home
                </Link>
              </div>

              <div className="space-y-1 mb-3">
                <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wide px-2 py-1">
                  Categories
                </p>
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeMobile}
                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors"
                  >
                    <div className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                      <img
                        src={cat.thumbUrl}
                        alt={cat.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    {cat.name}
                  </a>
                ))}
              </div>

              <div className="space-y-1 mb-4">
                <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wide px-2 py-1">
                  Shopping Guides
                </p>
                {guideSlugs.map((g) => (
                  <Link
                    key={g.slug}
                    to={`/guides/${g.slug}`}
                    onClick={closeMobile}
                    className={`flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors ${
                      location.pathname === `/guides/${g.slug}`
                        ? "bg-background-100 text-foreground-950 font-medium"
                        : "text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    <i className="ri-article-line text-base"></i>
                    {g.label}
                  </Link>
                ))}
              </div>

              <div className="flex flex-col gap-2">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="block w-full text-center px-4 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  PandaBuy Sheet
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="block w-full text-center px-4 py-3 rounded-full border-2 border-primary-500 text-primary-600 text-sm font-medium hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
                >
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}